/** Display maths for the weigh-in (the server computes the stored values the same way). */
import { Decimal, type DecimalString } from "@/lib/decimal";
import type { WorkspaceSettings } from "@/api/session";

export interface ReceivingTolerance {
  rel: DecimalString;
  absG: DecimalString;
}

export const DEFAULT_TOLERANCE: ReceivingTolerance = { rel: "0.02", absG: "0.02" };

/** `settings.inventory.receive_tolerance_rel` / `receive_tolerance_abs_g`, else 2 % or 0.02 g. */
export function receivingTolerance(settings: WorkspaceSettings | undefined): ReceivingTolerance {
  const inventory = (settings?.inventory ?? {}) as Record<string, unknown>;
  const rel = typeof inventory.receive_tolerance_rel === "string" ? inventory.receive_tolerance_rel : null;
  const absG = typeof inventory.receive_tolerance_abs_g === "string" ? inventory.receive_tolerance_abs_g : null;
  return {
    rel: rel && Decimal.tryFrom(rel) ? rel : DEFAULT_TOLERANCE.rel,
    absG: absG && Decimal.tryFrom(absG) ? absG : DEFAULT_TOLERANCE.absG,
  };
}

export interface WeighInResult {
  net: Decimal;
  difference: Decimal | null;
  tolerance: Decimal | null;
  underweight: boolean;
}

/** A missing tare can be estimated at receipt, but cannot verify the amount in the bottle. */
export function receivingWeightInput(
  gross: DecimalString | null,
  tare: DecimalString | null,
  labelled: DecimalString | null,
): { estimatedTare: Decimal | null; errors: { gross?: string; tare?: string; labelled?: string } } {
  const grossValue = Decimal.tryFrom(gross);
  const tareValue = Decimal.tryFrom(tare);
  if (grossValue === null) {
    return {
      estimatedTare: null,
      errors: tareValue === null ? {} : { gross: "Enter the gross weight, or leave the container tare empty." },
    };
  }
  if (tareValue !== null) {
    return {
      estimatedTare: null,
      errors: tareValue.compare(grossValue) > 0 ? { tare: "The tare can't be heavier than the gross weight" } : {},
    };
  }
  const label = Decimal.tryFrom(labelled);
  if (label === null || label.compare(0) <= 0) {
    return {
      estimatedTare: null,
      errors: { labelled: "Enter the labelled quantity to estimate the container tare, or enter the tare." },
    };
  }
  const estimatedTare = grossValue.minus(label);
  if (estimatedTare.isNegative()) {
    return {
      estimatedTare: null,
      errors: {
        gross: "Gross weight is less than the labelled quantity. Check the weights or enter the container tare.",
      },
    };
  }
  return { estimatedTare, errors: {} };
}

/** net = gross − tare; underweight when net falls short of the label by more than the tolerance. */
export function weighIn(
  gross: DecimalString | null,
  tare: DecimalString | null,
  labelled: DecimalString | null,
  tolerance: ReceivingTolerance = DEFAULT_TOLERANCE,
): WeighInResult | null {
  const grossValue = Decimal.tryFrom(gross);
  const tareValue = Decimal.tryFrom(tare);
  if (grossValue === null || tareValue === null) return null;
  const net = grossValue.minus(tareValue);
  const label = Decimal.tryFrom(labelled);
  if (label === null || label.isZero()) return { net, difference: null, tolerance: null, underweight: false };
  const relative = label.times(tolerance.rel);
  const allowed = relative.compare(tolerance.absG) >= 0 ? relative : Decimal.from(tolerance.absG);
  const difference = net.minus(label);
  return { net, difference, tolerance: allowed, underweight: difference.compare(allowed.negate()) < 0 };
}

/** "plus", "minus" or "zero", for styling a signed change. */
export function signOf(value: Decimal | DecimalString | null | undefined): "plus" | "minus" | "zero" {
  const decimal = value instanceof Decimal ? value : Decimal.tryFrom(value);
  if (decimal === null || decimal.isZero()) return "zero";
  return decimal.isNegative() ? "minus" : "plus";
}

/** Remaining grams for a stocktake weigh and the change from the ledger balance. */
export function stocktakeDelta(
  gross: DecimalString | null,
  tare: DecimalString | null,
  expected: DecimalString | null,
): { remaining: Decimal; delta: Decimal | null } | null {
  const grossValue = Decimal.tryFrom(gross);
  const tareValue = Decimal.tryFrom(tare);
  if (grossValue === null || tareValue === null) return null;
  const remaining = grossValue.minus(tareValue);
  const expectedValue = Decimal.tryFrom(expected);
  return { remaining, delta: expectedValue === null ? null : remaining.minus(expectedValue) };
}
